import React from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

function helperText(exportCount, sampleCount, showPreviewCount) {
  if (showPreviewCount && exportCount > sampleCount) {
    return `More data available: preview shows ${sampleCount} of ${exportCount} saved businesses. Export downloads the complete saved artifact CSV.`;
  }
  return 'Export downloads the complete saved artifact CSV.';
}

export default function ChatArtifactExportCard({
  exportAction,
  totalCount = 0,
  exportCount = totalCount,
  sampleCount = 0,
  showPreviewCount = true,
  isExporting = false,
  error = null,
  warning = null,
  onExport,
  children,
}) {
  if (!exportAction) return null;

  const enabled = exportAction.enabled !== false;
  const disabledReason = exportAction.disabled_reason || exportAction.disabledReason;
  const label = isExporting ? 'Exporting...' : exportAction.label || 'Export';
  const shouldRenderButton = enabled || sampleCount > 0;

  return (
    <Box
      sx={{
        pt: 1.25,
        borderTop: '1px solid',
        borderTopColor: 'divider',
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {enabled
          ? helperText(exportCount, sampleCount, showPreviewCount)
          : disabledReason || 'Export is unavailable for this result.'}
      </Typography>
      {(shouldRenderButton || children) && (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
          {shouldRenderButton && (
            <>
              <Box flex={1} minWidth={0} sx={{ flexBasis: { xs: '100%', sm: 'auto' } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0 }}>
                  Ready to export your results?
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {exportAction.cost_label || 'Download your list as a CSV when you are ready.'}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                startIcon={
                  isExporting
                    ? <CircularProgress size={16} color="inherit" />
                    : <FileDownloadOutlinedIcon fontSize="small" />
                }
                disabled={!enabled || isExporting}
                onClick={onExport}
                sx={{ minWidth: 108 }}
              >
                {label}
              </Button>
            </>
          )}
          {children}
        </Box>
      )}
      {warning?.message && (
        <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
          {warning.message}
        </Alert>
      )}
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
